import { query, collection, where } from 'firebase/firestore'
import { useMemo } from 'react'
import { db } from '../libs/Firebase.js'
import { Collections, Subcollections } from '../../../pure-js/libs/Collections.js'
import { DropinTicket } from '../../../pure-js/types/GrooverTypes.js'
import { useFirestoreQuery } from './useFirestoreQuery.js'
import { ReducerState } from './useFirestoreQueryHelper.js'
import { usePortalHostQueryGate } from './portalHostQueryGate'

/**
 * Hook to fetch drop-in tickets for a specific enrollment user using reactive queries.
 * Matches dancer app: same path (enrollment_users/{id}/dropin_tickets) and no orderBy.
 *
 * @param enrollmentUserId - The enrollment user ID from usePortalAuth, or null if not authenticated
 * @param clientId - Optional client to filter to (from useClientRouteHost()). When provided, adds
 *   a `where('clientId', '==', clientId)` filter — used by client-specific Portal subdomains (AC-1,
 *   COU-334). This is a single equality filter on an already user-scoped subcollection (not a
 *   collection group query), so it's covered by Firestore's automatic single-field indexing — no
 *   composite index needed. Omitted/undefined for the unified portal, which shows all clients' data.
 * @returns ReducerState with status, data, error, and isLoading properties
 */
export const useDropinTicketsForUser = (
  enrollmentUserId: string | null,
  clientId?: string
): ReducerState<DropinTicket[]> => {
  const { readyUserId, hostLoading } = usePortalHostQueryGate(enrollmentUserId)

  const firestoreQuery = useMemo(() => {
    if (!readyUserId) {
      // Return dummy collection to avoid permission errors when user is not authenticated
      // This prevents Firestore from attempting to query with null/undefined values
      return query(
        collection(db, `${Collections.ENROLLMENT_USERS}/dummy-dropin-tickets/${Subcollections.DROPIN_TICKETS}`)
      )
    }

    // Match dancer: no orderBy so Firestore returns docs in default order (same as dancer app)
    const ticketsCollection = collection(
      db,
      `${Collections.ENROLLMENT_USERS}/${readyUserId}/${Subcollections.DROPIN_TICKETS}`
    )
    return clientId ? query(ticketsCollection, where('clientId', '==', clientId)) : query(ticketsCollection)
  }, [readyUserId, clientId])

  const result = useFirestoreQuery<DropinTicket[]>(firestoreQuery, {
    fnName: 'useDropinTicketsForUser',
    enabled: !!readyUserId
  })

  return {
    ...result,
    isLoading: hostLoading || result.isLoading,
    status: hostLoading ? 'loading' : result.status
  }
}
